// Presentation and bounded client backoff only. The server remains the rate-limit authority.
const MAX_RETRY_SECONDS = 24 * 60 * 60
let retryUntil = 0

export function requestLanguage() {
  const pathname = typeof window !== 'undefined' && window.location ? window.location.pathname : ''
  return /^\/en(?:\/|$)/i.test(pathname) ? 'en' : 'fa'
}

export function isRateLimitError(error) {
  return !!error && [error.status, error.response && error.response.status, error.status_code].some(status => Number(status) === 429)
    || !!(error && error.code === 'RATE_LIMITED')
}

function header(headers, name) {
  if (!headers) return null
  if (typeof headers.get === 'function') return headers.get(name)
  const key = Object.keys(headers).find(value => value.toLowerCase() === name.toLowerCase())
  return key ? headers[key] : null
}

export function rateLimitMetadata(error, now = Date.now()) {
  const knownDeadline = Number(error && error.retryAt)
  if (Number.isFinite(knownDeadline) && knownDeadline > 0) {
    const seconds = Math.max(0, Math.ceil((knownDeadline - now) / 1000))
    if (seconds <= MAX_RETRY_SECONDS) return {retryAfterSeconds: seconds, retryAt: Math.max(now, knownDeadline)}
  }
  const headers = error && ((error.response && error.response.headers) || error.headers)
  const raw = header(headers, 'Retry-After')
  let seconds = null
  if (raw != null && String(raw).trim() !== '') {
    const value = String(raw).trim()
    if (/^\d+$/.test(value)) {
      seconds = Number(value)
    } else if (/[A-Za-z]/.test(value)) {
      const date = Date.parse(value)
      const serverDate = Date.parse(header(headers, 'Date'))
      if (Number.isFinite(date)) seconds = Math.max(0, Math.ceil((date - (Number.isFinite(serverDate) ? serverDate : now)) / 1000))
    }
  }
  // Ignore malformed/unbounded hints; never invent a deadline or schedule an automatic retry.
  if (!Number.isFinite(seconds) || seconds < 0 || seconds > MAX_RETRY_SECONDS) return {retryAfterSeconds: null, retryAt: null}
  return {retryAfterSeconds: seconds, retryAt: now + seconds * 1000}
}

export function rateLimitMessage(error, lang = 'fa', now = Date.now()) {
  if (!isRateLimitError(error)) return ''
  const {retryAfterSeconds} = rateLimitMetadata(error, now)
  if (retryAfterSeconds > 0) {
    const seconds = lang === 'fa' ? String(retryAfterSeconds).replace(/\d/g, d => '۰۱۲۳۴۵۶۷۸۹'[d]) : String(retryAfterSeconds)
    return lang === 'fa'
      ? `تعداد درخواست‌ها بیش از حد مجاز شده است. ${seconds} ثانیه صبر کن و سپس دوباره تلاش کن.`
      : `Too many requests. Wait ${seconds} seconds, then try again.`
  }
  return lang === 'fa'
    ? 'تعداد درخواست‌ها بیش از حد مجاز شده است. کمی صبر کن و سپس دوباره تلاش کن.'
    : 'Too many requests. Wait a moment, then try again.'
}

export function rememberRateLimit(error, now = Date.now()) {
  if (!isRateLimitError(error)) return null
  const metadata = rateLimitMetadata(error, now)
  if (metadata.retryAt != null) retryUntil = Math.max(retryUntil, metadata.retryAt)
  return metadata
}

export function currentRateLimitError(lang = requestLanguage(), now = Date.now()) {
  if (retryUntil <= now) return null
  const error = new Error()
  error.status = 429
  error.code = 'RATE_LIMITED'
  error.retryAt = retryUntil
  error.retryAfterSeconds = Math.ceil((retryUntil - now) / 1000)
  error.retryable = true
  error.message = rateLimitMessage(error, lang, now)
  return error
}
